import React, { useState } from 'react';
import { SearchIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { SEOHead } from '../components/SEOHead';
import { createFAQSchema } from '../utils/structuredData';
export const FaqPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const faqSections = [{
    title: 'Getting Started',
    questions: [{
      question: 'What ages is Story Bunny best for?',
      answer: 'Story Bunny is designed for children ages 3 to 6 — but anyone can enjoy using it.'
    }, {
      question: 'Does my child need to know how to read or write?',
      answer: 'No. The interface is simple and intuitive. With just a few taps, your child can create a story from start to finish.'
    }, {
      question: 'Can kids use it on their own?',
      answer: 'Yes. Story Bunny was made to be easy enough for kids to use independently — or as something fun to do together.'
    }, {
      question: 'What devices does it work on?',
      answer: 'Story Bunny runs in your browser on any device — phone, tablet, or computer. No app or installation required.'
    }]
  }, {
    title: 'AI & Technology',
    questions: [{
      question: 'Does Story Bunny use AI?',
      answer: "Yes. Story Bunny uses AI to generate the words and images in each story, based on your child's choices."
    }, {
      question: 'Is my child chatting with AI?',
      answer: "No. Story Bunny doesn't use chat or conversation features."
    }, {
      question: 'How are the illustrations made?',
      answer: 'The artwork is created using generative AI — each image is unique and matched to the story your child creates.'
    }]
  }, {
    title: 'Account & Privacy',
    questions: [{
      question: 'Do I need to create an account?',
      answer: "Yes — you'll need to create a free account to read your first story."
    }, {
      question: 'Will the story be saved?',
      answer: "When you're logged in, your story is automatically saved as you go."
    }, {
      question: 'What data do you collect?',
      answer: "We don't collect any personal data unless you create an account. We do collect anonymous usage data to help us improve the experience."
    }]
  }, {
    title: 'Story Creation',
    questions: [{
      question: 'Are the stories appropriate for all ages?',
      answer: 'Yes. Story Bunny is designed with young children in mind, and stories are always age-appropriate and child-friendly.'
    }, {
      question: 'Can I choose what kind of story it makes?',
      answer: 'Yes — you can choose the characters, setting, art style, and even describe what happens.'
    }, {
      question: 'Can I print or download a story?',
      answer: 'Not at this time.'
    }]
  }, {
    title: 'Technical',
    questions: [{
      question: 'Is this an app?',
      answer: 'No — Story Bunny is browser-based. Just open it and start creating.'
    }, {
      question: 'Can I lock the screen while my child is using it?',
      answer: <>
              Yes —{' '}
              <Link to="/lock-screen" className="text-[#D4A088] hover:underline" data-id="element-1280">
                click here to view our step-by-step instructions
              </Link>{' '}
            </>
    }]
  }, {
    title: 'Pricing',
    questions: [{
      question: 'How much does Story Bunny cost?',
      answer: "It's free to create your first story. After that, it's $7/month for 10 stories, or $1 per additional story."
    }, {
      question: "What's included in a subscription?",
      answer: 'A subscription includes 10 stories per month and access to your story library.'
    }, {
      question: 'Can I use Story Bunny without a subscription?',
      answer: 'Yes — you can buy individual story credits anytime.'
    }]
  }];
  // Flatten all FAQs for structured data
  const allFAQs = faqSections.flatMap(section => section.questions.map(q => ({
    question: q.question,
    answer: typeof q.answer === 'string' ? q.answer : q.question // Handle JSX answers
  })));
  const faqStructuredData = createFAQSchema(allFAQs);
  const filteredSections = faqSections.map(section => ({
    ...section,
    questions: section.questions.filter(q => q.question.toLowerCase().includes(searchTerm.toLowerCase()) || q.answer.toLowerCase().includes(searchTerm.toLowerCase()))
  })).filter(section => section.questions.length > 0);
  return <>
      <SEOHead title="FAQ - Frequently Asked Questions" description="Get answers to common questions about Story Bunny. Learn about our AI-powered storytelling platform, pricing, safety features, and how to create magical stories for children." canonical="https://storybunny.ai/faq" structuredData={faqStructuredData} data-id="element-1281" />
      <div className="flex-1 flex flex-col bg-[#FFFBF6] overflow-hidden" data-id="element-1282">
        {/* Header */}
        <div className="bg-[#FFFAE4] shadow-sm border-b border-[#E5C1A9] p-4" data-id="element-1283">
          <div className="max-w-7xl mx-auto" data-id="element-1284">
            <h1 className="text-2xl font-bold text-[#6B4B3E]" data-id="element-1285">FAQ</h1>
            <p className="text-sm text-[#6B4B3E]/75" data-id="element-1286">
              Frequently Asked Questions
            </p>
          </div>
        </div>
        {/* Search Bar */}
        <div className="max-w-3xl mx-auto w-full px-4 py-8" data-id="element-1287">
          <div className="relative" data-id="element-1288">
            <input type="text" placeholder="Search" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full h-14 pl-12 pr-4 rounded-full border-2 border-[#6B4B3E] text-[#6B4B3E] text-lg focus:outline-none focus:border-[#D4A088]" data-id="element-1289" />
            <SearchIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[#6B4B3E]" size={24} data-id="element-1290" />
          </div>
        </div>
        {/* FAQ Content */}
        <div className="flex-1 overflow-auto" data-id="element-1291">
          <div className="max-w-3xl mx-auto px-4 pb-16" data-id="element-1292">
            {filteredSections.map((section, index) => <div key={index} className="mb-12" data-id="element-1293">
                <h2 className="text-xl font-bold text-[#6B4B3E] mb-6" data-id="element-1294">
                  {section.title}
                </h2>
                <div className="space-y-4" data-id="element-1295">
                  {section.questions.map((faq, faqIndex) => <div key={faqIndex} className="bg-white rounded-xl p-6 shadow-sm" data-id="element-1296">
                      <h3 className="text-lg font-medium text-[#6B4B3E] mb-3" data-id="element-1297">
                        {faq.question}
                      </h3>
                      <p className="text-[#6B4B3E]/80" data-id="element-1298">{faq.answer}</p>
                    </div>)}
                </div>
              </div>)}
          </div>
          {/* CTA Section */}
          <div className="bg-white py-16" data-id="element-1299">
            <div className="max-w-3xl mx-auto px-4 text-center" data-id="element-1300">
              <h2 className="text-2xl font-bold text-[#6B4B3E] mb-4" data-id="element-1301">
                Still curious?
              </h2>
              <p className="text-lg text-[#6B4B3E]/80 mb-8" data-id="element-1302">
                The best way to learn is to try it.
              </p>
              <a href="#top" className="inline-flex items-center justify-center px-8 py-4 bg-[#D4A088] text-white rounded-full text-lg font-medium hover:bg-[#C28B73] transition-colors" data-id="element-1303">
                Click to Create
              </a>
            </div>
          </div>
        </div>
      </div>
    </>;
};